import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../../lib/api';

export interface Skill {
  skillType: string;
  enabled: boolean;
  config: Record<string, unknown>;
  organizationId: string;
  createdAt: string;
  lastUpdated: string;
  id: string;
  name?: string;
}

interface CreateSkillRequest {
  skillType: string;
  enabled: boolean;
  name?: string;
  config?: Record<string, unknown>;
  secrets?: Record<string, unknown>;
}

interface UpdateSkillRequest {
  enabled: boolean;
  config?: Record<string, unknown>;
  secrets?: Record<string, unknown>;
}

// Create a new skill
export const useCreateSkill = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: CreateSkillRequest) => {
      const { data } = await api.post<{ data: Skill }>(
        '/api/core/pleri/skills',
        request,
      );
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['skills'] }),
        queryClient.refetchQueries({ queryKey: ['pleriStatus'] }),
      ]);
      return data.data;
    },
  });
};

// Update an existing skill
export const useUpdateSkill = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      skillType,
      ...request
    }: UpdateSkillRequest & { skillType: string }) => {
      const { data } = await api.put<{ data: Skill }>(
        `/api/core/pleri/skills/${skillType}`,
        request,
      );
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['skills'] }),
        queryClient.refetchQueries({ queryKey: ['pleriStatus'] }),
      ]);
      return data.data;
    },
  });
};

// Get a single skill
export const useSkill = (skillType: string, enabled = true) => {
  return useQuery({
    queryKey: ['skills', skillType],
    queryFn: async () => {
      const { data } = await api.get<{ data: Skill }>(
        `/api/core/pleri/skills/${skillType}`,
      );
      return data.data;
    },
    enabled,
  });
};

// List all skills
export const useSkills = (enabled = true) => {
  return useQuery({
    queryKey: ['skills'],
    queryFn: async () => {
      const { data } = await api.get<{ data: Skill[] }>(
        '/api/core/pleri/skills',
      );
      return data.data;
    },
    enabled,
  });
};

// Delete a skill
export const useDeleteSkill = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (skillId: string) => {
      const { data } = await api.delete<{ data: Skill }>(
        `/api/core/pleri/skills/${skillId}`,
      );
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['skills'] }),
        queryClient.refetchQueries({ queryKey: ['pleriStatus'] }),
      ]);
      return data.data;
    },
  });
};
